/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  Button,
  Col,
  Description,
  FlexContainer,
  Label,
  Loader,
  ModalConfirm,
  Row,
  Spacer,
  Spinner,
  SVGIcon,
  TabContainer,
  TabsContent,
  Text,
  theme,
} from "@nordcloud/gnui";
import { useAccountV2Query } from "~/generated/graphql";
import {
  BreadcrumbsBox,
  ContactsInput,
  ErrorMessage,
  InputField,
} from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { useUuidValidator } from "~/hooks";
import { useRemoveAccount, useUpdateAccount } from "~/hooks/graphql";
import { showError, showSuccess } from "~/services/toast";
import {
  generateActionConfirmText,
  generateActionSuccessText,
  removeFromApolloCache,
} from "~/tools";
import { updateSchema } from "./AddAccount/validators";

const StyledEdit = styled.div`
  .cancel {
    line-height: 1.5rem;
  }
  .update {
    margin-left: 0.5rem;
    display: inline-flex;
    align-items: center;
    & > div {
      margin-right: 0.25rem;
    }
  }
`;

const SingleTab = styled.ul`
  background: ${theme.color.background.ui01};
  border-radius: 0.25rem;
  box-shadow: 0 3px 6px rgba(31, 30, 47, 0.1);

  li {
    background-color: ${theme.color.background.ui01};
    &:hover {
      background-color: ${theme.color.background.ui01};
      cursor: auto;
    }
  }
`;

type Account = {
  id: string;
  name: string;
  displayName: string;
  description: string;
  ownerId: string;
};

type RouteParams = {
  accountId: string;
};

export function AccountEditPage() {
  const [isModalConfirmOpen, setIsModalConfirmOpen] = React.useState(false);
  const { accountId = "" } = useParams<RouteParams>();

  const navigate = useNavigate();

  const [account, setAccount] = React.useState<Account>({
    id: "",
    name: "",
    displayName: "",
    description: "",
    ownerId: "",
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(updateSchema),
  });

  useUuidValidator(accountId);
  const { data, loading, error, refetch } = useAccountV2Query({
    variables: { id: accountId },
  });

  const [updateAccount, { data: updateAccountData, loading: loadingUpdate }] =
    useUpdateAccount();

  const [removeAccount, { loading: loadingRemove, error: errorRemove }] =
    useRemoveAccount({
      update: (cache) =>
        data?.accountV2 &&
        removeFromApolloCache(cache, { item: data?.accountV2 }),
    });

  React.useEffect(() => {
    setAccount({
      id: data?.accountV2?.id ?? "",
      name: data?.accountV2?.name ?? "",
      displayName: data?.accountV2?.displayName ?? "",
      description: data?.accountV2?.description ?? "",
      ownerId: data?.accountV2?.owner?.id ?? "",
    });
  }, [data]);

  React.useEffect(() => {
    reset({
      ownerId: data?.accountV2?.owner?.id || "",
    });
  }, [reset, data]);

  React.useEffect(() => {
    register("ownerId", { required: true });
  }, [register]);

  const onUpdate = async (values: Account) => {
    try {
      await updateAccount({
        id: account.id,
        displayName: values.displayName,
        description: values.description,
        ownerId: values.ownerId,
      });

      showSuccess(SUCCESS_TEXT.accountUpdated);

      navigate(-1);
    } catch (err) {
      showError(err.message, {
        autoClose: false,
      });
    }
    refetch();
  };

  const onDelete = async () => {
    try {
      setIsModalConfirmOpen(false);
      await removeAccount({ nid: accountId });
      navigate("/accounts", { replace: true });
      showSuccess(
        generateActionSuccessText(`The ${account.name}`)()("removed")()
      );
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const tags = [
    {
      color: "success",
      text: "Account",
    },
  ];

  const displayName =
    data?.accountV2?.displayName ?? data?.accountV2?.name ?? accountId;

  return (
    <>
      <Row>
        <Col>
          <StyledEdit>
            <ErrorMessage error={error || errorRemove} />
            <BreadcrumbsBox
              title={displayName}
              tags={tags}
              labels={[
                {
                  value: accountId,
                  label: displayName,
                },
              ]}
            >
              <FlexContainer justifyContent="flex-end">
                <Button
                  mr="1rem"
                  icon="trash"
                  severity="medium"
                  onClick={() => setIsModalConfirmOpen(true)}
                >
                  Delete Account
                </Button>
                <Button
                  mr={theme.spacing.spacing04}
                  form="update-application-form"
                  type="submit"
                >
                  <div css={{ display: "flex" }}>
                    <span
                      css={{
                        display: "flex",
                      }}
                    >
                      {!loadingUpdate &&
                        !updateAccountData?.updateAccountV2 && (
                          <SVGIcon name="edit" />
                        )}
                      {loadingUpdate && <Spinner color="white" />}
                      {!loadingUpdate && updateAccountData?.updateAccountV2 && (
                        <SVGIcon name="success" />
                      )}
                    </span>
                    Update Account
                  </div>
                </Button>
                <Link to={`/accounts/${accountId}`}>
                  <Button severity="low" icon="close" className="cancel" />
                </Link>
              </FlexContainer>
            </BreadcrumbsBox>
            <SingleTab>
              <TabContainer>
                <Text weight="medium" tag="div">
                  Account Settings
                </Text>
                <Text size="sm" color={theme.color.text.text02} tag="div">
                  Edit your account settings
                </Text>
              </TabContainer>
              <TabsContent>
                {loading || loadingRemove ? (
                  <Loader inContent />
                ) : (
                  <Row>
                    <form
                      style={{ width: "100%" }}
                      id="update-application-form"
                      onSubmit={handleSubmit(onUpdate)}
                    >
                      <Col lg={8}>
                        <InputField
                          label="Account ID"
                          {...register("id")}
                          disabled
                          value={account.id}
                          error={errors.id}
                        />
                        <InputField
                          disabled
                          label="Account Name"
                          {...register("name")}
                          error={errors.name}
                          value={account.name}
                        />
                        <InputField
                          label="Display Name"
                          {...register("displayName")}
                          error={errors.displayName}
                          value={account.displayName}
                          onChange={(e) => {
                            setAccount({
                              ...account,
                              displayName: e.target.value,
                            });
                            setValue("displayName", e.target.value);
                          }}
                        />
                        <Label required name="Account Owner" />
                        <ContactsInput
                          status={errors.ownerId ? "error" : "success"}
                          placeholder="Select account owner"
                          value={[account.ownerId]}
                          onChange={(id) => {
                            try {
                              setAccount({ ...account, ownerId: id });
                              setValue("ownerId", id);
                              clearErrors("ownerId");
                              showSuccess(SUCCESS_TEXT.accountOwnerAdded);
                            } catch {
                              showError(ERROR_TEXT.default);
                            }
                          }}
                        />
                        {errors.ownerId && (
                          <Description>Contact person is required.</Description>
                        )}
                        <Spacer height="1rem" />
                        <InputField
                          label="Account Description"
                          {...register("description")}
                          value={account.description}
                          error={errors.description}
                          onChange={(e) => {
                            setAccount({
                              ...account,
                              description: e.target.value,
                            });
                            setValue("description", e.target.value);
                          }}
                        />
                      </Col>
                    </form>
                  </Row>
                )}
              </TabsContent>
            </SingleTab>
          </StyledEdit>
        </Col>
      </Row>
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={() => onDelete()}
        actionLabel="Delete"
        contentLabel="Delete Account"
        onClose={() => setIsModalConfirmOpen(false)}
      >
        {generateActionConfirmText(account.name)("delete")()}
      </ModalConfirm>
    </>
  );
}
